import { useState, type CSSProperties, type ReactNode } from "react";
import { C, mono } from "./tokens";
import { Ico } from "./Ico";

// ── Screen scaffold ─────────────────────────────────────────────

export function Screen({
  children,
  padTop = 48,
  padBottom = 32,
  style = {},
}: {
  children: ReactNode;
  padTop?: number;
  padBottom?: number;
  style?: CSSProperties;
}) {
  return (
    <div style={{
      minHeight: "100dvh", boxSizing: "border-box",
      maxWidth: 460, margin: "0 auto",
      paddingTop: `calc(${padTop}px + env(safe-area-inset-top))`,
      paddingRight: "calc(24px + env(safe-area-inset-right))",
      paddingBottom: `calc(${padBottom}px + env(safe-area-inset-bottom))`,
      paddingLeft: "calc(24px + env(safe-area-inset-left))",
      background: C.bg, color: C.fg,
      position: "relative",
      ...style,
    }}>
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </div>
  );
}

// ── Top header (eyebrow title + actions) ───────────────────────

export function ScreenHeader({
  back, right, title,
}: {
  back?: () => void;
  right?: ReactNode;
  title?: string;
}) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      height: 36, marginBottom: 24,
    }}>
      <div style={{ height: 36, display: "flex", alignItems: "center", minWidth: 36 }}>
        {back && (
          <button onClick={back} style={btnIcon} aria-label="Zurück">
            <Ico name="arrowL" size={20} />
          </button>
        )}
        {!back && title && (
          <div style={{
            fontFamily: mono,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.18em",
            color: C.fg3, textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}>{title}</div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>{right}</div>
    </div>
  );
}

export const btnIcon: CSSProperties = {
  width: 36, height: 36, borderRadius: 12,
  background: C.surface, border: `1px solid ${C.border}`,
  display: "flex", alignItems: "center", justifyContent: "center",
  color: C.fg, cursor: "pointer", padding: 0,
};

// ── Type primitives ────────────────────────────────────────────

export function Display({
  children, size = "lg", style = {},
}: { children: ReactNode; size?: "lg" | "md" | "sm"; style?: CSSProperties }) {
  const sizes = { lg: 38, md: 30, sm: 24 };
  return (
    <h1 style={{
      fontSize: sizes[size], fontWeight: 700,
      letterSpacing: "-0.025em", lineHeight: 1.06,
      margin: 0, color: C.fg,
      ...style,
    }}>{children}</h1>
  );
}

export function Lede({ children, style = {} }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <p style={{
      fontSize: 16, lineHeight: 1.45, color: C.fg2,
      margin: "12px 0 0", maxWidth: 320, ...style,
    }}>{children}</p>
  );
}

export function Eyebrow({ children, style = {} }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      fontFamily: mono,
      fontSize: 10.5, fontWeight: 600, letterSpacing: "0.22em",
      textTransform: "uppercase", color: C.fg3,
      ...style,
    }}>{children}</div>
  );
}

// ── Button ─────────────────────────────────────────────────────

export type BtnKind = "primary" | "glass" | "ghost" | "text" | "danger";

export function Btn({
  kind = "primary",
  children,
  onClick,
  disabled,
  icon,
  full = true,
  style = {},
}: {
  kind?: BtnKind;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  full?: boolean;
  style?: CSSProperties;
}) {
  const base: CSSProperties = {
    height: 56, borderRadius: 16, padding: "0 22px",
    border: "1px solid transparent", cursor: "pointer",
    fontSize: 16, fontWeight: 600, letterSpacing: "-0.005em",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    width: full ? "100%" : undefined, boxSizing: "border-box",
    transition: "transform 120ms ease, opacity 120ms ease",
  };
  const variants: Record<BtnKind, CSSProperties> = {
    primary: {
      background: C.fg,
      color: "#000",
      border: "1px solid rgba(255,255,255,0.18)",
      boxShadow: "0 1px 0 rgba(255,255,255,0.4) inset, 0 8px 24px rgba(0,0,0,0.4)",
    },
    glass: {
      background: C.surface2,
      color: C.fg,
      border: `1px solid ${C.border}`,
      backdropFilter: "blur(20px) saturate(180%)",
      WebkitBackdropFilter: "blur(20px) saturate(180%)",
    },
    ghost: {
      background: "transparent",
      color: C.fg,
      border: `1px solid ${C.border}`,
    },
    text: {
      background: "transparent",
      color: C.fg2,
      border: "none", height: 44,
    },
    danger: {
      background: C.dangerBg,
      color: C.danger,
      border: "1px solid rgba(255,90,90,0.22)",
    },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        ...base, ...variants[kind],
        ...(disabled ? { opacity: 0.4, cursor: "not-allowed" } : {}),
        ...style,
      }}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

// ── Field + Input ──────────────────────────────────────────────

export function Field({
  label, hint, children, optional,
}: {
  label?: string;
  hint?: string;
  children: ReactNode;
  optional?: boolean;
}) {
  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      {label && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          marginBottom: 8, padding: "0 4px",
        }}>
          <span style={{
            fontSize: 12, fontWeight: 500, color: C.fg3,
            letterSpacing: "0.04em", textTransform: "uppercase",
          }}>
            {label}
          </span>
          {optional && <span style={{ fontSize: 11, color: C.fg4 }}>optional</span>}
        </div>
      )}
      {children}
      {hint && <div style={{ fontSize: 12, color: C.fg3, marginTop: 8, padding: "0 4px" }}>{hint}</div>}
    </label>
  );
}

export function Input({
  value, onChange, placeholder, type = "text",
  align = "left", mono: useMono, prefix, suffix,
  inputMode, step, autoFocus,
}: {
  value: string | number;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: string;
  align?: "left" | "right" | "center";
  mono?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  inputMode?: "decimal" | "numeric" | "text" | "email" | "tel" | "url" | "search";
  step?: string;
  autoFocus?: boolean;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{
      width: "100%", minWidth: 0, boxSizing: "border-box",
      height: 52, borderRadius: 14,
      background: C.surface,
      border: `1px solid ${focus ? C.borderHi : C.border}`,
      display: "flex", alignItems: "center", padding: "0 16px",
      transition: "border-color 150ms ease",
    }}>
      {prefix && <div style={{ color: C.fg3, fontSize: 14, marginRight: 8 }}>{prefix}</div>}
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        inputMode={inputMode}
        step={step}
        autoFocus={autoFocus}
        style={{
          flex: 1, minWidth: 0,
          background: "transparent", border: "none", outline: "none",
          color: C.fg, fontSize: 16,
          fontFamily: useMono ? mono : "inherit",
          textAlign: align,
          letterSpacing: useMono ? "0" : "-0.005em",
          padding: 0,
        }}
      />
      {suffix && <div style={{ color: C.fg3, fontSize: 14, marginLeft: 8 }}>{suffix}</div>}
    </div>
  );
}

// ── Card + dividers + labels ───────────────────────────────────

export function Card({ children, style = {} }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 20,
      padding: 20,
      ...style,
    }}>{children}</div>
  );
}

export function SectionLabel({
  children, right, style = {},
}: {
  children: ReactNode;
  right?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between",
      marginBottom: 12, padding: "0 4px", ...style,
    }}>
      <Eyebrow>{children}</Eyebrow>
      {right && <div style={{ fontSize: 12, color: C.fg3 }}>{right}</div>}
    </div>
  );
}

/**
 * Small spinner sized to sit inside a button, so an action can show progress
 * without its label changing under the user's finger.
 */
export function InlineSpinner({ size = 18, color = "#000" }: { size?: number; color?: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        display: "inline-block",
        borderRadius: "50%",
        border: `2px solid ${color}`,
        borderTopColor: "transparent",
        opacity: 0.8,
        animation: "spespin 0.8s linear infinite",
      }}
    />
  );
}
